import {
  ref,
  set,
  get,
  update,
} from "firebase/database";
import {
  trackAffiliateClick as apiTrackClick,
  trackAffiliateSignup as apiTrackSignup,
  completeAffiliateSignup as apiCompleteSignup,
  trackAffiliatePayment as apiTrackPayment,
  getAffiliateStats as apiGetStats,
  markNotificationRead,
} from "../api/affiliate";

// Thời gian cookie affiliate tồn tại (30 ngày)
const AFFILIATE_COOKIE_DURATION = 30 * 24 * 60 * 60 * 1000;

// Lưu affiliate ID và thông tin bổ sung vào cookie
export const setAffiliateCookie = (affiliateId, campaign = "", source = "") => {
  console.log("📝 Đang lưu cookie affiliate:", affiliateId);

  const expirationDate = new Date();
  expirationDate.setTime(expirationDate.getTime() + AFFILIATE_COOKIE_DURATION);

  // Sử dụng cả path=/ và SameSite=Lax để đảm bảo cookie hoạt động trên tất cả các trang
  document.cookie = `affiliateId=${affiliateId};expires=${expirationDate.toUTCString()};path=/;SameSite=Lax`;

  // Lưu thêm thông tin campaign và source
  if (campaign)
    document.cookie = `affiliateCampaign=${campaign};expires=${expirationDate.toUTCString()};path=/;SameSite=Lax`;
  if (source)
    document.cookie = `affiliateSource=${source};expires=${expirationDate.toUTCString()};path=/;SameSite=Lax`;

  // Lưu vào localStorage như một backup
  try {
    localStorage.setItem("affiliateId", affiliateId);
    if (campaign) localStorage.setItem("affiliateCampaign", campaign);
    if (source) localStorage.setItem("affiliateSource", source);
  } catch (error) {
    console.error("❌ Lỗi khi lưu affiliateId vào localStorage:", error);
  }

  console.log("✅ Đã lưu cookie affiliate thành công");
};

// Lấy affiliate ID từ cookie
export const getAffiliateCookie = () => {
  // Thử lấy từ cookie trước
  const cookies = document.cookie.split(";");
  let affiliateId = null;

  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === "affiliateId" && value) {
      affiliateId = value;
      console.log("📋 Đã tìm thấy affiliateId từ cookie:", affiliateId);
      break;
    }
  }

  // Nếu không tìm thấy trong cookie, thử lấy từ localStorage
  if (!affiliateId) {
    try {
      affiliateId = localStorage.getItem("affiliateId");
      if (affiliateId) {
        console.log("📋 Đã tìm thấy affiliateId từ localStorage:", affiliateId);
      }
    } catch (error) {
      console.error("❌ Lỗi khi lấy affiliateId từ localStorage:", error);
    }
  }

  if (!affiliateId) {
    console.log("⚠️ Không tìm thấy affiliateId từ cookie hoặc localStorage");
  }

  return affiliateId;
};

// Lấy chiến dịch affiliate từ cookie
export const getAffiliateCampaign = () => {
  // Thử lấy từ cookie
  const cookies = document.cookie.split(";");
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === "affiliateCampaign" && value) {
      return value;
    }
  }

  // Thử lấy từ localStorage
  try {
    const campaign = localStorage.getItem("affiliateCampaign");
    if (campaign) return campaign;
  } catch (error) {
    console.error("❌ Lỗi khi lấy affiliateCampaign từ localStorage:", error);
  }

  return "";
};

// Lấy nguồn affiliate từ cookie
export const getAffiliateSource = () => {
  // Thử lấy từ cookie
  const cookies = document.cookie.split(";");
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split("=");
    if (name === "affiliateSource" && value) {
      return value;
    }
  }

  // Thử lấy từ localStorage
  try {
    const source = localStorage.getItem("affiliateSource");
    if (source) return source;
  } catch (error) {
    console.error("❌ Lỗi khi lấy affiliateSource từ localStorage:", error);
  }

  return "";
};

// Xử lý affiliate ID từ URL
export const handleAffiliateLink = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const affiliateId = urlParams.get("ref");
  const campaign = urlParams.get("utm_campaign") || "";
  const source = urlParams.get("utm_source") || "";

  if (affiliateId) {
    console.log("🔗 Đã phát hiện affiliate link với ID:", affiliateId);

    // Đặt cookie affiliate
    setAffiliateCookie(affiliateId, campaign, source);

    // Tạo session ID duy nhất để ngăn ghi log lặp lại
    const currentUrl = window.location.href;
    const sessionClickKey = `affiliate_click_${affiliateId}_${currentUrl}`;

    // Kiểm tra xem đã ghi log cho URL và affiliate này trong phiên hiện tại chưa
    if (!sessionStorage.getItem(sessionClickKey)) {
      // Đánh dấu đã xử lý trong phiên này
      sessionStorage.setItem(sessionClickKey, "true");

      // Ghi log affiliate click
      logAffiliateClick(affiliateId, campaign, source);
    } else {
      console.log("⚠️ Bỏ qua duplicate affiliate click");
    }
  } else {
    console.log("ℹ️ Không phát hiện affiliate link trong URL");
  }
};

// Ghi nhật ký khi có người click vào link affiliate
const logAffiliateClick = async (affiliateId, campaign, source) => {
  try {
    // Sử dụng API mới thay vì Firebase trực tiếp
    const result = await apiTrackClick(
      affiliateId,
      campaign,
      source,
      window.location.href
    );

    if (result.success) {
      console.log("✅ Đã ghi nhận affiliate click thành công:", result.data);
    } else {
      console.error("❌ Lỗi khi ghi nhận affiliate click:", result.message);
    }
  } catch (error) {
    console.error("❌ Lỗi khi ghi nhật ký click affiliate:", error);
  }
};

// Kiểm tra và cập nhật thông tin affiliate khi user đăng ký
export const trackAffiliateSignup = async (userId) => {
  const affiliateId = getAffiliateCookie();
  const campaign = getAffiliateCampaign();
  const source = getAffiliateSource();

  console.log("🔍 Affiliate tracking - User ID:", userId);
  console.log("🔍 Affiliate tracking - Affiliate ID from cookie:", affiliateId);

  if (!affiliateId) {
    console.log(
      "❌ Affiliate tracking - Không tìm thấy affiliateId trong cookie"
    );
    return;
  }

  try {
    // Sử dụng API mới thay vì Firebase trực tiếp
    const result = await apiTrackSignup(userId, affiliateId, campaign, source);

    if (result.success) {
      console.log("✅ Đã ghi nhận đăng ký affiliate thành công:", result.data);
    } else {
      console.error("❌ Lỗi khi ghi nhận đăng ký affiliate:", result.message);
    }
  } catch (error) {
    console.error("❌ Lỗi khi theo dõi đăng ký affiliate:", error);
  }
};

// Hoàn thành đăng ký affiliate sau khi xác thực
export const completeAffiliateSignup = async (userId) => {
  const affiliateId = getAffiliateCookie();

  if (!affiliateId || !userId) {
    console.log("❌ Không đủ thông tin để hoàn thành đăng ký affiliate");
    return;
  }

  try {
    // Sử dụng API mới
    const result = await apiCompleteSignup(userId, affiliateId);

    if (result.success) {
      console.log(
        "✅ Đã hoàn thành đăng ký affiliate thành công:",
        result.data
      );
    } else {
      console.error("❌ Lỗi khi hoàn thành đăng ký affiliate:", result.message);
    }
  } catch (error) {
    console.error("❌ Lỗi khi hoàn thành đăng ký affiliate:", error);
  }
};

// Theo dõi thanh toán qua affiliate
export const trackAffiliatePayment = async (userId, amount) => {
  const affiliateId = getAffiliateCookie();
  const campaign = getAffiliateCampaign();
  const source = getAffiliateSource();

  if (!affiliateId || !userId) {
    console.log("❌ Không đủ thông tin để ghi nhận thanh toán affiliate");
    return;
  }

  try {
    // Sử dụng API mới
    const result = await apiTrackPayment(
      userId,
      affiliateId,
      amount,
      campaign,
      source
    );

    if (result.success) {
      console.log(
        "✅ Đã ghi nhận thanh toán affiliate thành công:",
        result.data
      );
      return result.data;
    } else {
      console.error(
        "❌ Lỗi khi ghi nhận thanh toán affiliate:",
        result.message
      );
      return null;
    }
  } catch (error) {
    console.error("❌ Lỗi khi ghi nhận thanh toán affiliate:", error);
    return null;
  }
};

// Tạo link affiliate cho user
export const generateAffiliateLink = (userId, campaign = "", source = "") => {
  const baseUrl = window.location.origin;
  let affiliateLink = `${baseUrl}/?ref=${userId}`;

  if (campaign) affiliateLink += `&utm_campaign=${campaign}`;
  if (source) affiliateLink += `&utm_source=${source}`;

  return affiliateLink;
};

// Lấy thống kê affiliate
export const getAffiliateStats = async (userId) => {
  try {
    // Sử dụng API mới
    const result = await apiGetStats(userId);

    if (result.success) {
      console.log("✅ Đã lấy thống kê affiliate thành công:", result.data);
      return result.data;
    } else {
      console.error("❌ Lỗi khi lấy thống kê affiliate:", result.message);
      return null;
    }
  } catch (error) {
    console.error("❌ Lỗi khi lấy thống kê affiliate:", error);
    return null;
  }
};

// Đánh dấu thông báo đã đọc
export const markAffiliateNotificationRead = async (userId, notificationId) => {
  try {
    // Sử dụng API mới
    const result = await markNotificationRead(userId, notificationId);

    if (result.success) {
      console.log("✅ Đã đánh dấu thông báo đã đọc thành công:", result.data);
      return true;
    } else {
      console.error("❌ Lỗi khi đánh dấu thông báo đã đọc:", result.message);
      return false;
    }
  } catch (error) {
    console.error("❌ Lỗi khi đánh dấu thông báo đã đọc:", error);
    return false;
  }
};
